import React from 'react';
import { CheckCircle, Clock, Users, Award, TrendingUp, FileText, Search } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { mockData } from '../data/mockData';

export const Acreditacion = () => {
  const { accreditationStages, achievementLevels } = mockData;

  const processSteps = [
    {
      icon: FileText,
      title: "Evaluación Interna",
      description: "Autoevaluación institucional con base en criterios establecidos"
    },
    {
      icon: Users,
      title: "Evaluación Externa",
      description: "Visita de expertos evaluadores externos certificados"
    },
    {
      icon: Search,
      title: "Análisis de Evidencias",
      description: "Revisión exhaustiva de documentos y procesos institucionales"
    },
    {
      icon: TrendingUp,
      title: "Plan de Mejoramiento",
      description: "Desarrollo de estrategias de mejora continua"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1434030216411-0b793f4b4173?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80"
            alt="University accreditation process"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-burgundy-50/65 to-white/75"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-burgundy-900 mb-6">
              Proceso de Acreditación
            </h1>
            <p className="text-xl text-burgundy-700 mb-8 max-w-3xl mx-auto">
              Un proceso integral y sistemático que garantiza los más altos estándares
              de calidad en la promoción de la salud universitaria
            </p>
          </div>
        </div>
      </section>

      {/* 5 Stages Process */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-burgundy-900 mb-6">
              Las 5 Etapas del Proceso
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Cada etapa está cuidadosamente diseñada para garantizar una evaluación
              exhaustiva y un mejoramiento continuo de la institución
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
            {accreditationStages.map((stage, index) => {
              const IconComponent = stage.icon === 'FileText' ? FileText :
                stage.icon === 'Search' ? Search :
                  stage.icon === 'Users' ? Users :
                    stage.icon === 'Award' ? Award : TrendingUp;
              return (
                <Card
                  key={stage.id}
                  className="text-center hover:shadow-lg transition-shadow duration-300 flex flex-col items-center h-full"
                >
                  <CardHeader className="flex flex-col items-center">
                    <div className="w-16 h-16 bg-burgundy-100 rounded-full flex items-center justify-center mb-4">
                      <IconComponent className="h-8 w-8 text-burgundy-700" />
                    </div>
                    <div className="bg-burgundy-700 text-white rounded-full w-8 h-8 flex items-center justify-center mb-2">
                      <span className="font-bold">{stage.id}</span>
                    </div>
                    <CardTitle className="text-lg text-burgundy-800 text-center min-h-[3rem] flex items-center justify-center">
                      {stage.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="flex flex-col flex-1 justify-between items-center">
                    <p className="text-gray-600 mb-4 text-center min-h-[3.5rem] flex items-center justify-center">
                      {stage.description}
                    </p>
                    <Badge variant="outline" className="text-burgundy-700 border-burgundy-300">
                      {stage.duration}
                    </Badge>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Decimal Model */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-burgundy-900 mb-6">
              Modelo Decimal de Evaluación
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Un sistema estructurado que garantiza una evaluación integral y sistemática
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
            <Card className="text-center">
              <CardContent className="pt-8">
                <div className="text-6xl font-bold text-burgundy-700 mb-4">10</div>
                <h3 className="text-xl font-semibold text-burgundy-800 mb-2">Rubros</h3>
                <p className="text-gray-600">Áreas principales de evaluación</p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-8">
                <div className="text-6xl font-bold text-burgundy-700 mb-4">100</div>
                <h3 className="text-xl font-semibold text-burgundy-800 mb-2">Ítems</h3>
                <p className="text-gray-600">Criterios específicos de evaluación</p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-8">
                <div className="text-6xl font-bold text-burgundy-700 mb-4">1000</div>
                <h3 className="text-xl font-semibold text-burgundy-800 mb-2">Indicadores</h3>
                <p className="text-gray-600">Medidas detalladas de cumplimiento</p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="pt-8">
                <div className="text-6xl font-bold text-burgundy-700 mb-4">10000</div>
                <h3 className="text-xl font-semibold text-burgundy-800 mb-2">Fuentes bibliográficas</h3>
                <p className="text-gray-600">Referencias y documentación de respaldo</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Key Processes */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-burgundy-900 mb-6">
              Procesos Clave
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {processSteps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                  <CardHeader>
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 bg-burgundy-100 rounded-lg flex items-center justify-center">
                        <IconComponent className="h-6 w-6 text-burgundy-700" />
                      </div>
                      <CardTitle className="text-xl text-burgundy-800">
                        {step.title}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <p className="text-gray-600">{step.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Achievement Levels */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-burgundy-900 mb-6">
              Niveles de Logro
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Reconocimiento progresivo basado en el nivel de implementación alcanzado
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {achievementLevels.map((level, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow duration-300">
                <CardHeader className="text-center">
                  <Badge className={`mx-auto mb-4 ${level.color}`}>
                    {level.percentage}
                  </Badge>
                  <CardTitle className="text-lg text-burgundy-800">
                    {level.level}
                  </CardTitle>
                </CardHeader>
                <CardContent className="text-center">
                  <p className="text-gray-600 text-sm">{level.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-burgundy-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            ¿Listo para Iniciar tu Proceso?
          </h2>
          <p className="text-lg text-burgundy-100 mb-8 max-w-2xl mx-auto">
            Únete a las universidades que ya están transformando la educación superior
            a través de la promoción de la salud
          </p>
          <Button
            size="lg"
            className="bg-white text-burgundy-900 hover:bg-gray-100 px-8 py-4 text-lg"
          >
            <Link to="/contacto">Solicitar Acceso a SIEVAS</Link>
          </Button>

          <hr className="mt-12 border-burgundy-700" />
        </div>

      </section>

    </div>
  );
};